import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { PMF_STEPS } from "../../data/sparkseed";
import { Search, Rocket, RefreshCw, LineChart, FileCheck2, Sprout } from "lucide-react";

const STEP_ICONS = [Search, Rocket, RefreshCw, LineChart, FileCheck2];
const ACCENT = "#B8D958";
const DARK = "#14401F";

/* Zig-zag positions for the icons (percent of viewBox) */
const X = [10, 30, 50, 70, 90];
const Y = [25, 55, 25, 55, 25];

/* Smooth cubic-bezier path through all icon centers */
const SMOOTH_PATH = (() => {
  let d = `M ${X[0]} ${Y[0]}`;
  for (let i = 0; i < X.length - 1; i++) {
    const x1 = X[i] + 8;
    const y1 = Y[i];
    const x2 = X[i + 1] - 8;
    const y2 = Y[i + 1];
    d += ` C ${x1} ${y1}, ${x2} ${y2}, ${X[i + 1]} ${Y[i + 1]}`;
  }
  return d;
})();

export default function VentureStudio() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!inView || hovered !== null) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % PMF_STEPS.length);
    }, 3200);
    return () => clearInterval(id);
  }, [inView, hovered]);

  return (
    <section
      ref={sectionRef}
      id="journey"
      data-testid="venture-studio-section"
      className="relative bg-[#DCE8C9] overflow-hidden"
    >
      {/* Background sprouts */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 0.3 } : { opacity: 0 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="absolute"
            style={{
              left: `${10 + ((i * 13) % 80)}%`,
              top: `${15 + ((i * 19) % 70)}%`,
              animation: `ss-shape-float ${6 + (i % 4)}s ease-in-out ${i * 0.4}s infinite`,
            }}
          >
            <Sprout
              className="text-[#14401F]"
              style={{
                width: 14 + (i % 3) * 6,
                height: 14 + (i % 3) * 6,
                opacity: 0.15,
              }}
            />
          </motion.span>
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <Eyebrow>The Journey</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
              From First <Italic>Spark</Italic> to Final Scale.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="border-l-2 border-[#14401F] pl-5">
              <p className="text-lg text-[#0F2818] font-semibold leading-snug">
                Five stages. One conviction.
              </p>
              <p className="text-sm text-[#4A5A4F] leading-relaxed mt-1">
                Capital, mentors, and network — calibrated for where the company actually is, not where the pitch deck says.
              </p>
            </div>
          </div>
        </div>

        {/* ============ Desktop wavy timeline ============ */}
        <div className="hidden lg:block relative">
          <div className="relative" style={{ height: 560 }}>
            {/* SVG smooth wavy path */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              <defs>
                <linearGradient id="pmf-wave" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor={DARK} />
                  <stop offset="0.5" stopColor={ACCENT} />
                  <stop offset="1" stopColor={DARK} />
                </linearGradient>
              </defs>

              {/* Ghost path */}
              <path
                d={SMOOTH_PATH}
                fill="none"
                stroke={DARK}
                strokeOpacity="0.18"
                strokeWidth="0.4"
                strokeDasharray="1.2 1.6"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />

              {/* Drawn wavy path */}
              <motion.path
                d={SMOOTH_PATH}
                fill="none"
                stroke="url(#pmf-wave)"
                strokeWidth="0.6"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
                transition={{ duration: 2.4, ease: "easeInOut" }}
                style={{ filter: "drop-shadow(0 0 5px rgba(184,217,88,0.45))" }}
              />

              {/* Traveling pulse */}
              {inView && (
                <circle r="1.2" fill={ACCENT}>
                  <animateMotion
                    dur="8s"
                    repeatCount="indefinite"
                    path={SMOOTH_PATH}
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </svg>

            {/* Icons positioned on the curve */}
            {PMF_STEPS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              const isActive = active === i;
              return (
                <div
                  key={`icon-${i}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
                  style={{ left: `${X[i]}%`, top: `${Y[i]}%` }}
                  onMouseEnter={() => {
                    setActive(i);
                    setHovered(i);
                  }}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setActive(i)}
                  data-testid={`pmf-step-${i}`}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{
                      delay: 0.4 + i * 0.18,
                      type: "spring",
                      stiffness: 260,
                      damping: 18,
                    }}
                  >
                    <IconNode Icon={Icon} index={i} isActive={isActive} />
                  </motion.div>
                </div>
              );
            })}

            {/* Cards: each card sits DIRECTLY BELOW its icon, anchored to it */}
            {PMF_STEPS.map((s, i) => {
              const isActive = active === i;
              return (
                <motion.div
                  key={`card-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 + i * 0.18, duration: 0.5 }}
                  className="absolute -translate-x-1/2 w-[200px] z-10"
                  style={{
                    left: `${X[i]}%`,
                    top: `calc(${Y[i]}% + 40px)`,
                  }}
                  onMouseEnter={() => {
                    setActive(i);
                    setHovered(i);
                  }}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setActive(i)}
                >
                  {/* Tiny connector dot between icon and card */}
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-[#14401F]/30" />
                  <StepCard step={s} isActive={isActive} />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ============ Mobile vertical timeline ============ */}
        <div className="lg:hidden relative">
          <div className="absolute left-7 top-0 bottom-0 w-px bg-[#14401F]/15" />
          <motion.div
            initial={{ scaleY: 0 }}
            animate={inView ? { scaleY: 1 } : {}}
            transition={{ duration: 1.6, ease: "easeOut" }}
            style={{ transformOrigin: "top" }}
            className="absolute left-7 top-0 bottom-0 w-px bg-gradient-to-b from-[#14401F] via-[#B8D958] to-[#14401F]"
          />
          <div className="space-y-8">
            {PMF_STEPS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              const isActive = active === i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
                  onClick={() => setActive(i)}
                  className="relative pl-20"
                  data-testid={`pmf-step-mobile-${i}`}
                >
                  <div className="absolute left-0 top-0 z-10">
                    <IconNode Icon={Icon} index={i} isActive={isActive} />
                  </div>
                  <StepCard step={s} isActive={isActive} mobile />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ============ Outcome strip ============ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6, duration: 0.7 }}
          className="mt-16 lg:mt-20 relative rounded-3xl overflow-hidden border border-[#14401F]/15"
          style={{
            background:
              "linear-gradient(135deg, #14401F 0%, #1a5026 50%, #14401F 100%)",
          }}
        >
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#B8D958]/15 blur-2xl pointer-events-none" />
          <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#E8A33D]/10 blur-2xl pointer-events-none" />

          <div className="relative p-7 sm:p-10 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#B8D958] font-bold mb-3">
                The Outcome
              </div>
              <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-white leading-snug">
                Walk in with an idea.{" "}
                <span className="italic text-[#B8D958]">Walk out</span> with a
                term sheet — and the numbers to back it.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-4">
              {[
                { v: "6", l: "Months" },
                { v: "0→1", l: "PMF" },
                { v: "100%", l: "Hands-on" },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 1.8 + i * 0.1 }}
                  className="text-center rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm py-4"
                >
                  <div className="font-serif-display text-2xl lg:text-3xl font-bold text-[#B8D958] leading-none">
                    {m.v}
                  </div>
                  <div className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-white/55 font-semibold">
                    {m.l}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------- Icon Node -------- */
function IconNode({ Icon, index, isActive }) {
  return (
    <div className="relative">
      {isActive && (
        <span
          className="absolute -inset-2 rounded-2xl"
          style={{
            background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)`,
            animation: "ss-pulse-glow 2.2s ease-in-out infinite",
          }}
        />
      )}
      <motion.div
        animate={isActive ? { scale: 1.08 } : { scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 18 }}
        className={`relative h-14 w-14 rounded-2xl flex items-center justify-center transition-colors duration-300 ${
          isActive
            ? "bg-[#14401F] shadow-[0_10px_30px_rgba(20,64,31,0.35)]"
            : "bg-[#F7F8EF] border-2 border-[#14401F]/20"
        }`}
      >
        <Icon
          className={`h-6 w-6 transition-colors ${
            isActive ? "text-[#B8D958]" : "text-[#14401F]"
          }`}
        />
        <span
          className={`absolute -top-2 -right-2 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold font-serif-display transition-colors ${
            isActive
              ? "bg-[#B8D958] text-[#0F2818]"
              : "bg-[#14401F] text-[#B8D958]"
          }`}
        >
          {index + 1}
        </span>
      </motion.div>
    </div>
  );
}

/* -------- Step Card -------- */
function StepCard({ step, isActive, mobile = false }) {
  return (
    <div
      className={`rounded-2xl p-4 lg:p-5 transition-all duration-300 cursor-pointer ${
        isActive
          ? "bg-[#F7F8EF] border border-[#14401F]/15 shadow-xl shadow-[#14401F]/8"
          : "bg-[#F7F8EF]/60 border border-[#14401F]/8 hover:bg-[#F7F8EF]/90"
      } ${mobile ? "w-full" : ""}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="h-1.5 w-1.5 rounded-full bg-[#B8D958]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#14401F] font-bold">
          {step.months}
        </span>
      </div>
      <h3 className="font-serif-display text-lg lg:text-xl font-bold text-[#0F2818] leading-tight mb-2">
        {step.title}
      </h3>
      <p className="text-xs lg:text-sm text-[#4A5A4F] leading-relaxed">
        {step.desc}
      </p>
      {isActive && (
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 3.2, ease: "linear" }}
          style={{ transformOrigin: "left" }}
          className="mt-3 h-0.5 bg-gradient-to-r from-[#B8D958] to-[#14401F] rounded-full"
        />
      )}
    </div>
  );
}
